import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import { fetchWithAuth } from '../helpers/fetchWithHeaders';
import { getRefreshToken, saveAccessToken, saveRefreshToken } from '../../utils/cookie.utils';
import { projectService } from './project.service';
import type {
  ConversationHeaders,
  FirstChunkHeaders,
  StreamingConversationParams,
  ConversationShell,
  ConversationStepMode,
} from '../../types/conversation.types';
import type { AgentStreamEvent, QuestionSetPayload } from '../../types/sse-events';
import { isAgentStreamEvent } from '../../types/sse-events';
import {
  deriveHasPrototypeFromFields,
  extractPrototyperBuildSessionFromInteraction,
} from '../../utils/prototyperMessageUtils';
import { prototypeKindFromSpec } from '../../utils/prototypeTitle';

export interface ChatRequest {
  message: string;
  mode?: 'clarify' | 'analyze' | 'design' | 'test';
  session_id?: string;
  tmp_conversation_id?: string;
  conversation_id?: string;
  project_id?: string;
  attachment_ids?: string[];
  skip_analysis?: boolean;
}

export interface ChatResponse {
  response: string;
  conversation_id?: string;
  [key: string]: any;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract conversation headers from HTTP response
 */
const extractConversationHeaders = (response: Response): ConversationHeaders => {
  const getHeader = (name: string): string | null => {
    return response.headers.get(name) || response.headers.get(name.toLowerCase());
  };

  const reflection = getHeader('X-Reflection') === 'true';
  const clarification = getHeader('X-Clarification') === 'true';
  const repeated = getHeader('X-Repeated') === 'true';
  const prototyper = getHeader('X-Prototyper') === 'true';
  const readyToGenerate = getHeader('X-Ready-To-Generate');
  const isReadyToCreate =
    getHeader('X-Ready-To-Create') === 'true' || getHeader('X-Ready-To-Create') === '1';
  const isReadyToCreateAccount =
    getHeader('X-Ready-To-Create-Account') === 'true' ||
    getHeader('X-Ready-To-Create-Account') === '1';
  const detectedLanguageHeader = getHeader('X-Detected-Language');
  const detectedLanguage: 'rtl' | 'ltr' | undefined =
    detectedLanguageHeader === 'rtl' || detectedLanguageHeader === 'ltr'
      ? detectedLanguageHeader
      : undefined;

  return {
    reflection: reflection || false,
    clarification: clarification || false,
    repeated: repeated || false,
    prototyper: prototyper || false,
    readyToGenerate:
      readyToGenerate &&
      readyToGenerate !== 'null' &&
      readyToGenerate !== 'none' &&
      readyToGenerate !== 'None'
        ? readyToGenerate
        : undefined,
    isReadyToCreate: isReadyToCreate || false,
    isReadyToCreateAccount: isReadyToCreateAccount || false,
    detectedLanguage,
  };
};

/**
 * Build a rich HTTP error with status and message from response body
 */
const buildHttpError = async (response: Response): Promise<Error & { status?: number }> => {
  const defaultMessage = `HTTP error! status: ${response.status}`;
  let message = defaultMessage;

  try {
    const bodyText = await response.text();
    if (bodyText) {
      try {
        const data = JSON.parse(bodyText);
        // The API pairs a short category (`error`) with the specific reason
        // (`message`) — e.g. "Invalid answer" + which question failed and why.
        // Keep both: the category alone is not actionable when it's shown to
        // the founder (the question card renders this verbatim).
        const category = typeof data?.error === 'string' ? data.error.trim() : '';
        const detail = typeof data?.message === 'string' ? data.message.trim() : '';
        if (category && detail && category !== detail) message = `${category}: ${detail}`;
        else message = category || detail || defaultMessage;
      } catch {
        message = bodyText;
      }
    }
  } catch {
    // Ignore parsing errors and fall back to default message
  }

  const error = new Error(message) as Error & { status?: number };
  error.status = response.status;
  return error;
};

/**
 * Ensure a scope exists before the first scoped conversation turn.
 */
export const ensureProjectScope = async (projectId: string): Promise<string> => {
  const scopes = await projectService.getStartupScopes(projectId);
  if (scopes.length > 0) {
    return scopes[0].id;
  }
  const created = await projectService.createScope(projectId, { name: 'Default' });
  return created.id;
};

/**
 * Create a conversation shell (no AI stream) before uploads or first message.
 */
export const createConversationShell = async (
  projectId: string,
  scopeId: string,
  mode: ConversationStepMode,
  title?: string
): Promise<ConversationShell> => {
  // Uses the dedicated non-streaming shell endpoint. The streaming create
  // endpoint requires a `user_message`, so it can't be used to make an empty
  // conversation for pre-message attachment uploads.
  const response = await apiClient.post<ConversationShell>(
    ApiUrls.createScopeConversationShell(projectId, scopeId),
    {
      mode,
      ...(title ? { title } : {}),
    }
  );
  return response.data;
};

/**
 * Empty Discussion Room conversation so chat attachments can bind before turn 1.
 * Nest does not create room conversations; this is the AI shell.
 */
export const createDiscussionConversationShell = async (
  projectId: string,
  roomId: string
): Promise<{ id: string }> => {
  const response = await apiClient.post<{ id: string; mode?: string }>(
    ApiUrls.createDiscussionConversationShell(projectId, roomId),
    { mode: 'strategy' }
  );
  if (!response.data?.id) {
    throw new Error('Discussion conversation shell was not created');
  }
  return { id: response.data.id };
};

/**
 * Upload a chat attachment bound to an existing conversation.
 */
export const uploadChatAttachment = async (
  conversationId: string,
  file: File,
  llmFamily?: string
): Promise<{ id: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('conversation_id', conversationId);
  if (llmFamily) {
    formData.append('llm_family', llmFamily);
  }

  const response = await apiClient.post(ApiUrls.chatAttachments, formData, {
    headers: { 'Content-Type': undefined as any },
  });
  const data = response.data;
  const id = data?.id || data?.attachmentId;
  if (!id) {
    throw new Error('No attachment ID received from server');
  }
  return { id };
};

/**
 * Determine the correct API URL based on request parameters
 */
const getConversationURL = (request: StreamingConversationParams): string => {
  // Discussion turn 1 (new row, or a shell id in the body) is the room create
  // stream. A later turn uses the conversation id on llm-interactions below.
  if (request.discussion_first_turn) {
    if (!request.project_id || !request.discussion_room_id) {
      throw new Error(
        'project_id and discussion_room_id are required to start a discussion conversation'
      );
    }
    return ApiUrls.createDiscussionConversation(request.project_id, request.discussion_room_id);
  }
  if (request.conversation_id) {
    return ApiUrls.conversations(request.conversation_id);
  }
  if (request.project_id && request.scope_id) {
    return ApiUrls.createScopeConversation(request.project_id, request.scope_id);
  }
  if (request.project_id) {
    throw new Error('scope_id is required when starting a new scoped conversation');
  }
  throw new Error(
    'A project and scope are required to start a conversation. Temporary conversations are no longer supported.'
  );
};

/**
 * Clean request payload — strip URL/path fields and removed body fields.
 */
const cleanRequestPayload = (request: StreamingConversationParams): Record<string, unknown> => {
  const {
    session_id,
    project_id: _projectId,
    scope_id: _scopeId,
    // Room id is a path segment on turn 1, and unknown to the llm-interactions
    // schema on later turns. Never send it in the body.
    discussion_room_id: _discussionRoomId,
    discussion_first_turn: discussionFirstTurn,
    conversation_id,
    ...body
  } = request;

  return {
    ...body,
    project_id: undefined,
    // Shell continuation puts the shell id in the turn-1 body. Later turns
    // carry the id in the llm-interactions URL, so it stays out of that body.
    ...(discussionFirstTurn && conversation_id
      ? { conversation_id }
      : { conversation_id: undefined }),
    session_id: request.project_id || request.conversation_id ? undefined : session_id,
  };
};

/**
 * Fetch with auth and automatic 401 token refresh handling
 * Mimics the axios interceptor behavior for streaming requests
 */
let refreshPromise: Promise<boolean> | null = null;

const fetchWithAuthAndRetry = async (url: string, init?: RequestInit): Promise<Response> => {
  const response = await fetchWithAuth(url, init);

  // Handle 401 - attempt token refresh and retry
  if (response.status === 401) {
    // Don't retry if this was already a refresh request
    if (url.includes('auth/refresh')) {
      console.warn('401 on refresh endpoint - not retrying');
      return response;
    }

    const attemptRefresh = async (): Promise<boolean> => {
      const rt = getRefreshToken();
      if (!rt) return false;

      try {
        const { authService } = await import('./auth.service');
        const result = await authService.refreshToken(rt);
        saveAccessToken(result.access_token);
        saveRefreshToken(result.refresh_token);
        return true;
      } catch (e) {
        return false;
      } finally {
        refreshPromise = null;
      }
    };

    // Deduplicate refresh requests
    if (!refreshPromise) {
      refreshPromise = attemptRefresh();
    }

    const refreshed = await refreshPromise;

    if (refreshed) {
      // Retry the original request with new token
      const retryResponse = await fetchWithAuth(url, init);
      return retryResponse;
    }

    // Refresh failed - return the 401 response (error will be handled by caller)
    console.warn('❌ 401 Unauthorized - refresh failed or no refresh token');
  }

  return response;
};

// ============================================================================
// STREAMING CONVERSATION SERVICE
// ============================================================================

/**
 * Create a streaming conversation
 * Handles Server-Sent Events (SSE) or chunked transfer encoding
 */
export const createConversationStream = async (
  request: StreamingConversationParams,
  onEvent: (
    event: AgentStreamEvent,
    meta?: { firstChunkHeaders?: FirstChunkHeaders; allHeaders?: ConversationHeaders }
  ) => void,
  onError: (error: Error) => void,
  onComplete: (conversationId: string, headers: ConversationHeaders) => void,
  signal?: AbortSignal
): Promise<void> => {
  try {
    const url = getConversationURL(request);
    const response = await fetchWithAuthAndRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream, text/plain, application/json',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(cleanRequestPayload(request)),
      signal,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const conversationId =
      response.headers.get('X-Conversation-ID') || response.headers.get('conversation-id') || '';
    const llmInteractionId =
      response.headers.get('X-LLM-Interaction-ID') ||
      response.headers.get('x-llm-interaction-id') ||
      undefined;
    const headers = extractConversationHeaders(response);

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const contentType = response.headers.get('content-type') ?? '';
    const isSse = contentType.includes('text/event-stream');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let isFirstChunk = true;
    let buffer = '';
    let legacyFullText = '';

    const firstChunkMeta = () => ({
      firstChunkHeaders: {
        conversationId,
        llmInteractionId,
        headers: { detectedLanguage: headers.detectedLanguage },
      },
      allHeaders: headers,
    });

    const emitEvent = (event: AgentStreamEvent) => {
      const meta = isFirstChunk ? firstChunkMeta() : undefined;
      if (isFirstChunk) {
        isFirstChunk = false;
      }
      onEvent(event, meta);
    };

    const processSseLine = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) return;

      const raw = trimmed.substring(6);
      if (!raw || raw === '[DONE]') return;

      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }

      if (!isAgentStreamEvent(parsed)) return;
      emitEvent(parsed);
    };

    try {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const readWithTimeout = () => {
        return Promise.race([
          reader.read(),
          new Promise<{ done: boolean; value?: Uint8Array }>((_, reject) => {
            timeoutId = setTimeout(() => {
              reject(new Error('Stream read timeout'));
            }, 300000);
          }),
        ]);
      };

      while (true) {
        try {
          const { done, value } = await readWithTimeout();
          if (timeoutId) clearTimeout(timeoutId);

          if (done) {
            if (isSse) {
              if (buffer.trim()) {
                processSseLine(buffer);
              }
            } else {
              // Flush any remaining plain-text bytes, then synthesize terminal result
              if (buffer) {
                legacyFullText += buffer;
                emitEvent({ type: 'token', payload: { text: buffer } });
                buffer = '';
              }
              emitEvent({
                type: 'result',
                payload: { content: legacyFullText },
              });
            }
            onComplete(conversationId, headers);
            break;
          }

          const chunk = decoder.decode(value, { stream: true });

          if (isSse) {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              processSseLine(line);
            }
          } else {
            // Legacy text/plain — stream as synthetic tokens
            if (chunk) {
              legacyFullText += chunk;
              emitEvent({ type: 'token', payload: { text: chunk } });
            }
          }
        } catch (error) {
          if (error instanceof Error && error.message.includes('timeout')) {
            console.warn('Stream timeout, attempting fallback...');
            await createConversationFallback(request, onEvent, onError, onComplete, signal);
            return;
          }
          throw error;
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
};

/**
 * Fallback to non-streaming conversation creation
 * Used when streaming is not supported or fails
 */
export const createConversationFallback = async (
  request: StreamingConversationParams,
  onEvent: (
    event: AgentStreamEvent,
    meta?: { firstChunkHeaders?: FirstChunkHeaders; allHeaders?: ConversationHeaders }
  ) => void,
  onError: (error: Error) => void,
  onComplete: (conversationId: string, headers: ConversationHeaders) => void,
  signal?: AbortSignal
): Promise<void> => {
  try {
    const response = await fetchWithAuthAndRetry(getConversationURL(request), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cleanRequestPayload(request)),
      signal,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const conversationId =
      response.headers.get('X-Conversation-ID') || response.headers.get('conversation-id') || '';
    const headers = extractConversationHeaders(response);
    const fullResponse = await response.text();

    const meta = {
      firstChunkHeaders: {
        conversationId,
        headers: { detectedLanguage: headers.detectedLanguage },
      },
      allHeaders: headers,
    };

    const words = fullResponse.split(' ');
    for (let i = 0; i < words.length; i++) {
      const chunk = i === 0 ? words[i] : ' ' + words[i];
      onEvent({ type: 'token', payload: { text: chunk } }, i === 0 ? meta : undefined);
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    onEvent({ type: 'result', payload: { content: fullResponse } });
    onComplete(conversationId, headers);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
};

// ============================================================================
// CONVERSATION HISTORY & UTILITY FUNCTIONS
// ============================================================================

/**
 * Load conversation history from API
 * Converts API format to message format
 */
/**
 * Map the inline `artifacts` array on a history interaction to the message's
 * `artifact` (pill + side panel). Structured artifacts (JTBD, persona, 5 whys,
 * HMW) carry `structured_content` (the JSON the card renders from); generic
 * artifacts have only markdown `content`, previewed as-is. We deliberately do
 * NOT rely on the markdown for the typed artifacts.
 */
const mapInteractionArtifact = (artifacts: unknown): any | undefined => {
  if (!Array.isArray(artifacts) || artifacts.length === 0) return undefined;
  const a = artifacts[0] as Record<string, any>;
  if (!a) return undefined;
  const structured =
    a.structured_content && typeof a.structured_content === 'object' ? a.structured_content : null;
  return {
    id: a.id,
    name: a.artifact_type || 'artifact',
    content: typeof a.content === 'string' ? a.content : '',
    format: structured ? 'json' : 'markdown',
    structuredContent: structured,
    demoUrl: a.demo_url ?? null,
    fileFormat: a.file_format ?? null,
    fileName: a.file_name ?? null,
    mediaType: a.media_type ?? null,
    fileStatus: a.file_status ?? null,
  };
};

/**
 * Result of loading a conversation's history.
 *
 * `pendingQuestions` restores the interactive MCQ card after a reload/navigation:
 * the backend attaches it to the **last** interaction only, and only while an
 * interactive question set asked there is still unanswered through the form.
 * Same shape as the live SSE `question` event payload, so the card renderer and
 * the answer flow (which sends `question_set_id` back) are reused unchanged. It
 * is `null` when nothing is pending.
 */
export interface ConversationHistoryResult {
  messages: any[];
  pendingQuestions: QuestionSetPayload | null;
}

export const loadConversationHistory = async (
  conversationId: string,
  projectId?: string | null
): Promise<ConversationHistoryResult> => {
  try {
    const url = ApiUrls.conversations(conversationId);

    const response = await apiClient.get(url);
    const interactions = response.data;

    // Sort by interactionOrder to maintain conversation flow
    const sortedInteractions = interactions.sort(
      (a: any, b: any) => a.interactionOrder - b.interactionOrder
    );

    // Convert API format to conversation messages format
    const messages: any[] = [];

    sortedInteractions.forEach((interaction: any) => {
      // Add user message (include messages with only attachments)
      if (
        (interaction.userMessage ||
          (interaction.attachments_ids && interaction.attachments_ids.length > 0)) &&
        !interaction.isHiddenUserQuery
      ) {
        messages.push({
          id: interaction.id + '_user', // Ensure string ID
          content: interaction.userMessage || '',
          attachments_ids: interaction.attachments_ids,
          role: 'user',
        });
      }

      // Add assistant message
      if (interaction.llmReply) {
        const buildSession = extractPrototyperBuildSessionFromInteraction(interaction);
        const prototyperFields = {
          prototypeReady: interaction.prototypeReady === true,
          prototyper_demo_link_id: interaction.prototyper_demo_link_id || null,
          buildTimeline: buildSession.buildTimeline ?? undefined,
          buildStatus: buildSession.buildStatus,
          prototypeSnapshotId: buildSession.prototypeSnapshotId ?? undefined,
        };

        messages.push({
          id: interaction.id,
          content: interaction.llmReply,
          role: 'assistant',
          actionType: interaction.actionType,
          nodeType: interaction.nodeType,
          mode: interaction.mode,
          detectedLanguage: interaction.detectedLanguage,
          llmInteractionId: interaction.id, // Store the real backend UUID for feedback
          isLiked: interaction.isLiked, // Store feedback state from backend
          prototyper_demo_link_id: prototyperFields.prototyper_demo_link_id,
          artifact_demo_link_id: interaction.artifact_demo_link_id || null,
          buildTimeline: prototyperFields.buildTimeline,
          buildStatus: prototyperFields.buildStatus,
          prototypeDisplayName: buildSession.prototypeDisplayName ?? undefined,
          // The prototype's own name, resolved server-side from the turn's spec and the
          // stored `chef_project_links.title`, so a reload shows what the live pill showed.
          prototypeTitle: (interaction.prototypeTitle ?? interaction.prototype_title) as
            | string
            | null
            | undefined,
          // Only the mode is kept off the turn's spec, not the spec: it is the title's type
          // segment, and the whole product definition on every message would be paid for on
          // every render.
          prototypeKind: prototypeKindFromSpec(interaction.generationText),
          prototypeVersionIndex: buildSession.prototypeVersionIndex,
          prototypeSnapshotId: prototyperFields.prototypeSnapshotId,
          prototypeReady: prototyperFields.prototypeReady,
          hasPrototype: deriveHasPrototypeFromFields(prototyperFields),
          // Artifact (pill + card) from the same interaction — structured JSON
          // for typed artifacts, markdown for generic.
          artifact: mapInteractionArtifact(interaction.artifacts),
        });
      }
    });

    // The backend adds `pendingQuestions` to the last interaction only (absent on
    // earlier ones and when nothing is pending). Restore it so the MCQ card
    // survives a reload / navigation away and back.
    const lastInteraction = sortedInteractions[sortedInteractions.length - 1];
    const pendingQuestions: QuestionSetPayload | null =
      (lastInteraction && lastInteraction.pendingQuestions) || null;

    return { messages, pendingQuestions };
  } catch (error) {
    console.error('Failed to load conversation history:', error);
    throw error;
  }
};

class ConversationService {
  /**
   * Send a chat message to the AI
   */
  async sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await apiClient.post<ChatResponse>(ApiUrls.chat, request);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get conversation history by conversation ID
   */
  async getConversationHistory(conversationId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.conversations(conversationId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get conversation by ID
   */
  async getConversationById(conversationId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.conversationsById(conversationId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get tokens for a conversation
   */
  async getTokens(interactionId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.tokens(interactionId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get request cost
   */
  async getRequestCost(requestId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.requestCost(requestId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Send like/dislike feedback for an LLM interaction.
   * Accepts either a llmInteractionId (for persisted conversation interactions)
   * or a tmpLlmInteractionId (for temporary interactions). Body: { isLiked: boolean }
   */
  async sendLlmInteractionFeedback(params: {
    messageID?: string;
    isLiked: boolean;
    projectId?: string;
  }): Promise<any> {
    try {
      const { messageID, isLiked } = params;

      if (!messageID) {
        throw new Error('No LLM interaction id was given');
      }

      const url = ApiUrls.llmInteractionLike(messageID);
      const response = await apiClient.patch(url, { isLiked });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response) {
      const message =
        error.response.data?.message || error.response.data?.error || 'Request failed';
      const err = new Error(message) as Error & { status?: number };
      err.status = error.response.status;
      return err;
    }

    // Network error
    return new Error('Network error. Please check your connection.');
  }
}

export const conversationService = new ConversationService();
export default conversationService;
